import {Box, Table, Tbody, Td, Text, Th, Thead, Tr} from '@chakra-ui/react';
import {LigneMatching} from 'api-types/ffconnect';
import {prixLigneAEteCompare} from '@/backoffice/domains/PdfOrderImport/utils';
import {formatQuantiteAvecCoefficient, toDecimalFr} from 'ff-common';
import {TFunction} from 'i18next';
import {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';

/**
 * Import PDF : Table de comparaison du matching en dessous de la ligne de panier :
 * ce que le PDF demandait et de ce que le matching en a déduit.
 * Dépliée sous la barre de synthèse d'une ligne de panier,
 * pour que l'opérateur voie d'où vient un état « Alerte » ou
 * « Bloquant ». Les valeurs viennent du rapport, jamais du panier courant.
 */
export const TableComparaisonMatching: React.FC<{
  ligneMatching: LigneMatching;
}> = ({ligneMatching}) => {
  const {t} = useTranslation();
  const {donnees_extraites, item_matche, matching} = ligneMatching;
  const tolede = item_matche.tolede;
  // Sans article matché, il n'y a rien à comparer
  if (!tolede) return null;

  const libelleMatche = matching.libelle?.libelle_matche;
  const tokensCommuns = matching.libelle?.tokens_communs ?? []; //mots présents dans les deux libellés.
  const tokensManquants = matching.libelle?.tokens_manquants ?? []; // mots du PDF qu'on n'a pas retrouvés côté catalogue => expliquent pourquoi le score n'est pas de 100 %.

  // Le code retenu, suivi des codes secondaires qui ont pu servir au
  // rapprochement (le PDF porte souvent le code générique ou le code client).
  const codesSecondaires = [
    tolede.code_article_client &&
      t('back_office.pdf_order_import.match.code_article_client_info', {
        code: tolede.code_article_client,
      }),
    tolede.code_generique &&
      t('back_office.pdf_order_import.match.code_generique_info', {
        code: tolede.code_generique,
      }),
  ].filter(Boolean);

  const quantiteInterpretee =
    matching.quantite?.quantite_interpretee ??
    donnees_extraites.quantite_commandee;
  const multipleCommande = tolede.multiple_de_commande_client;

  // Les deux prix d'une ligne : son prix unitaire et son montant (quantité x prix unitaire).
  // La colonne « Valeur PDF » restitue ce que le document portait, même quand la comparaison n'a pas eu lieu ; seul le verdict en dépend.
  // La comparaison n'a pas lieu dans deux cas : la ligne ne porte pas de prix, ou l'article n'en a pas au catalogue pour cette livraison.
  const prixTotalPdfPresent = Boolean(donnees_extraites.prix_total_extrait);
  const prixUnitairePdfPresent = Boolean(
    donnees_extraites.prix_unitaire_extrait,
  );
  const prixCatalogueConnu =
    tolede.prix_par_livraison?.[0]?.montant_ht !== undefined;
  const prixTotalComparable = prixLigneAEteCompare(matching.prix_ligne_total);

  const rows: {
    champ: string;
    valeurPdf: ReactNode;
    deduction: ReactNode;
    resultat: ReactNode;
  }[] = [
    /* Code article */
    {
      champ: t('back_office.pdf_order_import.match.champ_code_article'),
      valeurPdf: donnees_extraites.code_article_extrait,
      deduction:
        codesSecondaires.length > 0
          ? `${tolede.code_article} (${codesSecondaires.join(', ')})`
          : tolede.code_article,
      resultat: (
        <BadgeResultat
          estOk={
            matching.code?.etat === 'exact'
              ? true
              : matching.code?.etat === 'ko'
                ? false
                : undefined
          }
        />
      ),
    },
    /* Libellé */
    {
      champ: t('back_office.pdf_order_import.match.champ_libelle'),
      valeurPdf: surlignerTokens(
        donnees_extraites.libelle_extrait,
        tokensCommuns,
        tokensManquants,
      ),
      // Côté catalogue, seuls les tokens communs sont surlignés : ce qui manque
      // au libellé du PDF n'a pas de sens ici.
      deduction: libelleMatche
        ? surlignerTokens(libelleMatche, tokensCommuns, [])
        : '-',
      resultat:
        matching.libelle?.score_normalise !== undefined ? (
          <BadgeSimilarite
            score={matching.libelle.score_normalise}
            etat={matching.libelle.etat}
          />
        ) : (
          <BadgeResultat estOk={undefined} />
        ),
    },
    /* unité de commande */
    {
      champ: t('back_office.pdf_order_import.match.champ_unite_commande'),
      valeurPdf: donnees_extraites.unite_commande,
      deduction: afficherUnite(t, tolede.unite_de_commande_client),
      // Une unité incohérente n'est pas un échec : le matcher a converti
      // (« 10 pièces » → « 2,5 kg »), il faut le signaler, pas le rejeter.
      resultat: (
        <BadgeResultat
          estOk={matching.unite?.est_coherente}
          enAlerte={matching.unite?.est_coherente === false}
        />
      ),
    },
    /* Unité de facturation */
    {
      champ: t('back_office.pdf_order_import.match.champ_unite_facturation'),
      valeurPdf: donnees_extraites.unite_facturation ?? '-',
      deduction: afficherUnite(t, tolede.unite_de_facturation),
      resultat: (
        <BadgeResultat
          estOk={matching.unite?.est_coherente}
          enAlerte={matching.unite?.est_coherente === false}
        />
      ),
    },
    /* Quantité */
    {
      champ: t('back_office.pdf_order_import.match.champ_quantite'),
      valeurPdf: `${donnees_extraites.quantite_commandee} ${afficherUnite(t, donnees_extraites.unite_commande, donnees_extraites.quantite_commandee)}`,
      deduction: (
        <>
          {formatQuantiteAvecCoefficient({
            quantite: quantiteInterpretee,
            uniteCommande: tolede.unite_de_commande_client,
            uniteFacturation: tolede.unite_de_facturation,
            coefficientCommande:
              tolede.coefficient_commande_client_vers_facturation ?? 1,
            formatUnite: (unite: string, count: number) =>
              t(`unite.${unite}`, {count}),
          })}
          {multipleCommande !== 1 &&
            `, ${t('back_office.pdf_order_import.match.quantite_multiple_info', {multiple: multipleCommande})}`}
        </>
      ),
      // Même logique que les unités : une quantité réinterprétée se signale.
      resultat: (
        <BadgeResultat
          estOk={matching.quantite?.est_coherent}
          enAlerte={matching.quantite?.est_coherent === false}
        />
      ),
    },
    /* Prix unitaire */
    {
      champ: t('back_office.pdf_order_import.match.champ_prix_unitaire'),
      valeurPdf: prixUnitairePdfPresent
        ? `${toDecimalFr(donnees_extraites.prix_unitaire_extrait, 2, 3)} €`
        : '-',
      deduction: formatPrixAvecEcart(
        tolede.prix_par_livraison?.[0]?.montant_ht,
        matching.prix_unitaire?.ecart_pourcentage,
      ),
      resultat: (
        <BadgeResultat
          estOk={
            prixUnitairePdfPresent && prixCatalogueConnu
              ? matching.prix_unitaire?.identique
              : undefined
          }
        />
      ),
    },
    /* Prix total */
    {
      champ: t('back_office.pdf_order_import.match.champ_prix_total'),
      valeurPdf: prixTotalPdfPresent
        ? `${toDecimalFr(donnees_extraites.prix_total_extrait, 2, 3)} €`
        : '-',
      deduction: formatPrixAvecEcart(
        ligneMatching.prix_total_catalogue,
        matching.prix_ligne_total?.ecart_pourcentage,
      ),
      resultat: (
        <BadgeResultat
          estOk={
            prixTotalComparable
              ? matching.prix_ligne_total?.etat === 'ok'
              : undefined
          }
          enAlerte={
            prixTotalComparable && matching.prix_ligne_total?.etat === 'alerte'
          }
        />
      ),
    },
    /* Présence dans le cadencier */
    {
      champ: t('back_office.pdf_order_import.match.champ_cadencier'),
      valeurPdf: '-',
      deduction:
        matching.dans_cadencier === undefined
          ? '-'
          : t(matching.dans_cadencier ? 'global.yes' : 'global.no'),
      resultat: <BadgeResultat estOk={undefined} />,
    },
  ];

  return (
    <>
      {/* Ligne du bon de commande telle qu'elle a été lue, avant toute interprétation. */}
      {donnees_extraites.texte_entree && (
        <Box
          px='3'
          py='2'
          fontSize='xs'
          fontStyle='italic'
          color='gray.dark'
          whiteSpace='pre-wrap'
          wordBreak='break-word'
        >
          <Text
            as='span'
            fontWeight='bold'
          >
            {t('back_office.pdf_order_import.match.champ_texte_entree')}:{' '}
          </Text>
          {donnees_extraites.texte_entree}
        </Box>
      )}
      <Box
        overflowX='auto'
        bg='gray.50'
      >
        <Table
          size='sm'
          variant='simple'
          sx={{
            'th, td': {
              color: 'gray.dark',
              fontSize: 'xs',
              borderColor: 'gray.light',
              verticalAlign: 'top',
            },
          }}
        >
          <Thead>
            <Tr bg='gray.light'>
              <Th>{t('back_office.pdf_order_import.match.colonne_champ')}</Th>
              <Th>
                {t('back_office.pdf_order_import.match.colonne_valeur_pdf')}
              </Th>
              <Th>
                {t('back_office.pdf_order_import.match.colonne_valeur_matchee')}
              </Th>
              <Th w='90px'>
                {t('back_office.pdf_order_import.match.colonne_statut')}
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row) => (
              <Tr key={row.champ}>
                <Td
                  fontWeight='500'
                  minW='150px'
                >
                  {row.champ}
                </Td>
                <Td minW='200px'>{row.valeurPdf}</Td>
                <Td minW='250px'>{row.deduction}</Td>
                <Td w='90px'>{row.resultat}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </>
  );
};

// Surligne mot à mot : en vert ce que les deux libellés ont en commun, en rouge
// ce que le PDF demandait et qu'on n'a pas retrouvé. C'est ce qui rend un score
// de similarité compréhensible d'un coup d'œil.
const surlignerTokens = (
  texte: string,
  tokensCommuns: string[],
  tokensManquants: string[],
): ReactNode => {
  return texte.split(/\s+/).map((mot, index) => {
    const motNormalise = mot.toLowerCase().replace(/[.,;:!?]/g, '');
    const estCommun = tokensCommuns.some(
      (token) => token.toLowerCase() === motNormalise,
    );
    const estManquant = tokensManquants.some(
      (token) => token.toLowerCase() === motNormalise,
    );
    return (
      <Text
        key={index}
        as='span'
        px={estCommun || estManquant ? 1 : 0}
        borderRadius='sm'
        bg={
          estCommun
            ? 'green.success_bg'
            : estManquant
              ? 'red.warning_bg'
              : undefined
        }
        color={
          estCommun ? 'green.success' : estManquant ? 'red.fail' : undefined
        }
      >
        {mot}{' '}
      </Text>
    );
  });
};

const afficherUnite = (
  t: TFunction,
  unite: string | undefined,
  count = 1,
): string => {
  if (!unite) return '-';
  if (unite === 'UNKNOWN') {
    return t('back_office.pdf_order_import.match.unite_inconnue');
  }
  return t(`unite.${unite}`, {count});
};

// Prix catalogue, suivi de l'écart avec le PDF quand il est significatif.
const formatPrixAvecEcart = (
  prix: number | undefined,
  ecartPourcentage: number | undefined,
): string => {
  if (prix === undefined) return '-';
  const prixFormate = `${toDecimalFr(prix, 2, 3)} €`;
  if (
    ecartPourcentage === undefined ||
    Math.abs(parseFloat(ecartPourcentage.toFixed(1))) === 0
  ) {
    return prixFormate;
  }
  const signe = ecartPourcentage >= 0 ? '+' : '';
  return `${prixFormate} (${signe}${ecartPourcentage.toFixed(1)}%)`;
};

// Résultat d'une ligne de la table. `undefined` = rien à conclure, on n'affiche
// pas un KO là où le matching ne s'est pas prononcé.
const BadgeResultat: React.FC<{estOk?: boolean; enAlerte?: boolean}> = ({
  estOk,
  enAlerte,
}) => {
  const {t} = useTranslation();
  const config = enAlerte
    ? {bg: 'yellow.warning', color: 'yellow.warning_text', texte: '!'}
    : estOk === true
      ? {
          bg: 'green.success_bg',
          color: 'green.success',
          texte: t('panier.matching_resultat_ok'),
        }
      : estOk === false
        ? {
            bg: 'red.warning_bg',
            color: 'red.fail',
            texte: t('panier.matching_resultat_ko'),
          }
        : {bg: undefined, color: 'gray.dark', texte: '-'};

  return (
    <Box
      px='1.5'
      py='0.5'
      borderRadius='4px'
      fontSize='xs'
      fontWeight={config.bg ? 'bold' : undefined}
      bg={config.bg}
      color={config.color}
      display='inline-block'
    >
      {config.texte}
    </Box>
  );
};

// Score de similarité des libellés, coloré selon le verdict du matching.
const BadgeSimilarite: React.FC<{
  score: number;
  etat?: 'exact' | 'partiel' | 'ko';
}> = ({score, etat}) => {
  const config =
    etat === 'ko'
      ? {bg: 'red.warning_bg', color: 'red.fail'}
      : etat === 'partiel'
        ? {bg: 'yellow.warning', color: 'yellow.warning_text'}
        : {bg: 'green.success_bg', color: 'green.success'};

  return (
    <Box
      px='1.5'
      py='0.5'
      borderRadius='4px'
      fontSize='xs'
      fontWeight='bold'
      bg={config.bg}
      color={config.color}
      display='inline-block'
    >
      {Math.round(score * 100)}%
    </Box>
  );
};
